import { useEffect, useRef } from 'react';
import { useUnsavedStore } from '../store/unsaved.js';

// Hooks côté écran pour le registre des brouillons (store/unsaved.ts).

export interface UnsavedGuardOptions {
  /** Identifiant stable de l'écran / du formulaire. */
  id: string;
  /** Vrai tant qu'il y a quelque chose à perdre. */
  dirty: boolean;
  label: string;
  summary?: string;
  /** Enregistre ; doit rejeter en cas d'échec. Absent = non enregistrable. */
  save?: () => Promise<void>;
  /** Remet l'écran à l'état propre. */
  discard?: () => void;
}

/**
 * Déclare un brouillon tant que `dirty` est vrai. Les callbacks sont lus
 * au moment de l'appel (refs), donc l'écran peut les passer inline.
 */
export function useUnsavedGuard(opts: UnsavedGuardOptions): void {
  const ref = useRef(opts);
  ref.current = opts;
  const { id, dirty, label, summary } = opts;
  const hasSave = !!opts.save;
  const hasDiscard = !!opts.discard;
  const setEntry = useUnsavedStore((s) => s.setEntry);
  const clearEntry = useUnsavedStore((s) => s.clearEntry);

  useEffect(() => {
    if (!dirty) {
      clearEntry(id);
      return undefined;
    }
    setEntry({
      id,
      label,
      summary,
      save: hasSave ? () => ref.current.save!() : undefined,
      discard: hasDiscard ? () => ref.current.discard?.() : undefined,
    });
    return () => clearEntry(id);
  }, [id, dirty, label, summary, hasSave, hasDiscard, setEntry, clearEntry]);
}

/**
 * Pose un « flusher » pour une sauvegarde automatique différée : appelé
 * quand on quitte (société, page, onglet) et au démontage, pour que le
 * dernier délai en attente ne soit jamais perdu.
 */
export function useAutosaveFlush(id: string, flush: () => void): void {
  const ref = useRef(flush);
  ref.current = flush;
  const setFlusher = useUnsavedStore((s) => s.setFlusher);
  const clearFlusher = useUnsavedStore((s) => s.clearFlusher);
  useEffect(() => {
    setFlusher(id, () => ref.current());
    return () => {
      ref.current();
      clearFlusher(id);
    };
  }, [id, setFlusher, clearFlusher]);
}
